import { EXPLORA_DESTINATIONS } from "./explora-destinations";
/**
 * Client-safe search & indexing utilities for unified tours.
 *
 * - Curated city catalog (slug ↔ display name + region + match terms).
 * - Multi-field haystack (title, location, summary, description, categories).
 * - Multi-token AND matching with accent folding.
 * - Suggestion generator (cities + "Tours en X" + product titles).
 * - Sort helpers for destacados / precio / popularidad / duración.
 */

export interface SearchableTour {
  id: string;
  title: string;
  summary: string | null;
  description: string | null;
  images: string[];
  priceFrom: number | null;
  currency: string | null;
  durationMinutes: number | null;
  location: string | null;
  categories: string[];
  available: boolean;
}

// ---------------- City catalog ------------------------------------------------

export interface CityEntry {
  name: string;
  region: string;
  terms: string[]; // lowercased / accent-folded match terms
  tagline: string;
}

const RAW_CITIES: Record<string, CityEntry> = {
  cancun: {
    name: "Cancún",
    region: "Quintana Roo",
    terms: ["cancun", "cancún", "zona hotelera cancun"],
    tagline: "Caribe turquesa, vida nocturna y aventura submarina.",
  },
  tulum: {
    name: "Tulum",
    region: "Quintana Roo",
    terms: ["tulum"],
    tagline: "Ruinas frente al mar, cenotes y la mejor energía boho.",
  },
  "playa-del-carmen": {
    name: "Playa del Carmen",
    region: "Quintana Roo",
    terms: ["playa del carmen", "playa carmen", "5ta avenida", "quinta avenida"],
    tagline: "Quinta avenida, snorkel y la puerta a Cozumel.",
  },
  cozumel: {
    name: "Cozumel",
    region: "Quintana Roo",
    terms: ["cozumel"],
    tagline: "El paraíso del buceo y los arrecifes del Caribe.",
  },
  "isla-mujeres": {
    name: "Isla Mujeres",
    region: "Quintana Roo",
    terms: ["isla mujeres"],
    tagline: "Playa Norte, ballena tiburón y atardeceres irrepetibles.",
  },
  holbox: {
    name: "Holbox",
    region: "Quintana Roo",
    terms: ["holbox"],
    tagline: "Isla virgen, flamencos y noches bioluminiscentes.",
  },
  bacalar: {
    name: "Bacalar",
    region: "Quintana Roo",
    terms: ["bacalar", "laguna de los siete colores"],
    tagline: "La laguna de los siete colores y los estromatolitos.",
  },
  "chichen-itza": {
    name: "Chichén Itzá",
    region: "Yucatán",
    terms: ["chichen", "chichén", "chichen itza", "chichén itzá"],
    tagline: "Maravilla del mundo maya, cenotes y Valladolid.",
  },
  valladolid: {
    name: "Valladolid",
    region: "Yucatán",
    terms: ["valladolid"],
    tagline: "Pueblo mágico, cenotes secretos y gastronomía yucateca.",
  },
  merida: {
    name: "Mérida",
    region: "Yucatán",
    terms: ["merida", "mérida"],
    tagline: "Capital blanca de Yucatán, haciendas y ruta puuc.",
  },
  oaxaca: {
    name: "Oaxaca",
    region: "Oaxaca",
    terms: ["oaxaca"],
    tagline: "Mezcal, mole y los pueblos artesanos más vivos de México.",
  },
  cdmx: {
    name: "Ciudad de México",
    region: "CDMX",
    terms: ["ciudad de mexico", "ciudad de méxico", "cdmx", "mexico city", "df"],
    tagline: "Megalópolis con museos, mercados y rooftops legendarios.",
  },
  "los-cabos": {
    name: "Los Cabos",
    region: "Baja California Sur",
    terms: ["cabo", "los cabos", "san jose del cabo", "cabo san lucas"],
    tagline: "El Arco, ballenas grises y desierto frente al Pacífico.",
  },
  "puerto-vallarta": {
    name: "Puerto Vallarta",
    region: "Jalisco",
    terms: ["vallarta", "puerto vallarta"],
    tagline: "Malecón, Sierra Madre y playas vírgenes en Yelapa.",
  },
};

// Merge the whole Mexico destination registry, including places without tours.
for (const d of EXPLORA_DESTINATIONS) {
  RAW_CITIES[d.citySlug] ??= { name: d.name, region: d.state, terms: [...d.keywords, d.name], tagline: d.description.es };
}

export const CITY_CATALOG: Readonly<Record<string, CityEntry>> = RAW_CITIES;

export const CITY_SLUGS: string[] = Object.keys(RAW_CITIES);

// ---------------- State (entidad federativa) catalog --------------------------

export interface StateEntry {
  name: string;
  terms: string[];
  tagline: string;
  cities: string[]; // city slugs that belong to this state
}

const RAW_STATES: Record<string, StateEntry> = {
  "quintana-roo": {
    name: "Quintana Roo",
    terms: ["quintana roo", "riviera maya", "caribe mexicano"],
    tagline: "El Caribe mexicano: cenotes, ruinas y arena blanca.",
    cities: ["cancun", "tulum", "playa-del-carmen", "cozumel", "isla-mujeres", "holbox", "bacalar"],
  },
  yucatan: {
    name: "Yucatán",
    terms: ["yucatan", "yucatán", "peninsula de yucatan"],
    tagline: "Mundo maya, cenotes secretos y haciendas henequeneras.",
    cities: ["chichen-itza", "valladolid", "merida"],
  },
  oaxaca: {
    name: "Oaxaca",
    terms: ["oaxaca"],
    tagline: "Mezcal, mole y los pueblos artesanos más vivos de México.",
    cities: ["oaxaca"],
  },
  cdmx: {
    name: "Ciudad de México",
    terms: ["ciudad de mexico", "ciudad de méxico", "cdmx", "mexico city", "df"],
    tagline: "Megalópolis con museos, mercados y rooftops legendarios.",
    cities: ["cdmx"],
  },
  "baja-california-sur": {
    name: "Baja California Sur",
    terms: ["baja california sur", "bcs", "la paz"],
    tagline: "Mar de Cortés, ballenas y desierto frente al Pacífico.",
    cities: ["los-cabos"],
  },
  jalisco: {
    name: "Jalisco",
    terms: ["jalisco", "guadalajara"],
    tagline: "Mariachi, tequila y playas del Pacífico.",
    cities: ["puerto-vallarta"],
  },
};

export const STATE_CATALOG: Readonly<Record<string, StateEntry>> = RAW_STATES;
export const STATE_SLUGS: string[] = Object.keys(RAW_STATES);

/** Map a city slug back to its state slug, if any. */
export function stateForCity(citySlug: string): string | null {
  for (const [slug, st] of Object.entries(RAW_STATES)) {
    if (st.cities.includes(citySlug)) return slug;
  }
  return null;
}

// ---------------- Normalization & indexing ------------------------------------

/** Lowercase + strip diacritics for accent-insensitive search. */
export function normalize(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

interface IndexedProduct<T extends SearchableTour = SearchableTour> {
  product: T;
  haystack: string;
  cityHits: string[]; // slugs of cities that match this product
}

/**
 * Manual overrides: map a product id (Bokun id or internal UUID) to one or more
 * city slugs. Use when product text fields don't contain the city name.
 */
const PRODUCT_CITY_OVERRIDES: Record<string, string[]> = {
  "1028954": ["cancun"],
  "1224631": ["cancun"],
  "1011695": ["cancun"],
  "995305": ["puerto-vallarta"],
  "1223628": ["puerto-vallarta"],
};

const indexCache = new WeakMap<readonly SearchableTour[], IndexedProduct[]>();

function buildIndex<T extends SearchableTour>(products: readonly T[]): IndexedProduct<T>[] {
  const cached = indexCache.get(products as readonly SearchableTour[]);
  if (cached) return cached as IndexedProduct<T>[];

  const built = products.map<IndexedProduct<T>>((p) => {
    const fields = [
      p.title,
      p.location ?? "",
      p.summary ?? "",
      p.description ?? "",
      ...(p.categories ?? []),
    ];
    const haystack = normalize(fields.filter(Boolean).join(" \u00b7 "));
    const cityHits: string[] = [];
    for (const [slug, city] of Object.entries(RAW_CITIES)) {
      if (city.terms.some((t) => haystack.includes(normalize(t)))) {
        cityHits.push(slug);
      }
    }
    const overrides = PRODUCT_CITY_OVERRIDES[String(p.id)];
    if (overrides) {
      for (const slug of overrides) {
        if (RAW_CITIES[slug] && !cityHits.includes(slug)) cityHits.push(slug);
      }
    }
    return { product: p, haystack, cityHits };
  });
  indexCache.set(products as readonly SearchableTour[], built as IndexedProduct[]);
  return built;
}

/** Defensive dedup by stable `id` (preserves order). */
export function dedupById<T extends { id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const it of items) {
    if (!it?.id || seen.has(it.id)) continue;
    seen.add(it.id);
    out.push(it);
  }
  return out;
}

// ---------------- Search ------------------------------------------------------

/**
 * Multi-token AND search across all product fields + city terms.
 * Empty query returns the original list (useful for default browsing).
 */
export function matchProducts<T extends SearchableTour>(
  products: readonly T[],
  query: string,
): T[] {
  const q = normalize(query);
  if (!q) return dedupById(products as readonly { id: string }[]) as T[];
  const tokens = q.split(/\s+/).filter(Boolean);
  const index = buildIndex(products);

  const matched = index
    .filter(({ haystack, cityHits }) => {
      return tokens.every((tok) => {
        if (haystack.includes(tok)) return true;
        // Match through city alias: query "cancun" matches a product whose
        // location is "Zona Hotelera" but city catalog maps it to cancun.
        return cityHits.some((slug) => {
          const city = RAW_CITIES[slug];
          return (
            normalize(slug).includes(tok) ||
            normalize(city.name).includes(tok) ||
            city.terms.some((t) => normalize(t).includes(tok))
          );
        });
      });
    })
    .map((entry) => entry.product);
  return dedupById(matched);
}

/** Products linked to a city slug via the curated term list. */
export function productsForCity<T extends SearchableTour>(
  products: readonly T[],
  citySlug: string,
): T[] {
  const index = buildIndex(products);
  return dedupById(index.filter((e) => e.cityHits.includes(citySlug)).map((e) => e.product));
}

/** Products linked to a state slug: union of its cities + state-term matches. */
export function productsForState<T extends SearchableTour>(
  products: readonly T[],
  stateSlug: string,
): T[] {
  const st = RAW_STATES[stateSlug];
  if (!st) return [];
  const index = buildIndex(products);
  const stateTerms = st.terms.map(normalize);
  return dedupById(
    index
      .filter((e) => {
        if (e.cityHits.some((c) => st.cities.includes(c))) return true;
        return stateTerms.some((t) => e.haystack.includes(t));
      })
      .map((e) => e.product),
  );
}

// ---------------- Suggestions / autocomplete ----------------------------------

export type Suggestion =
  | { kind: "city"; label: string; sublabel: string; slug: string; count: number }
  | { kind: "phrase"; label: string; sublabel: string; query: string }
  | { kind: "product"; label: string; sublabel: string; productId: string };

/**
 * Build autocomplete suggestions for the search box. Returns up to ~8 items:
 *   • matching cities (with product counts)
 *   • "Tours en X" / "Excursiones en X" phrase shortcuts
 *   • a few top matching products by title
 */
export function getSuggestions(
  query: string,
  products: readonly SearchableTour[],
  limit = 8,
): Suggestion[] {
  const q = normalize(query);
  if (!q) return [];

  const index = buildIndex(products);
  const suggestions: Suggestion[] = [];

  // 1. Cities matching by slug / name / aliases.
  const cityMatches = Object.entries(RAW_CITIES)
    .map(([slug, city]) => {
      const haystack = normalize(`${slug} ${city.name} ${city.region} ${city.terms.join(" ")}`);
      const score = haystack.includes(q) ? (haystack.startsWith(q) ? 0 : 1) : -1;
      return { slug, city, score };
    })
    .filter((m) => m.score >= 0)
    .sort((a, b) => a.score - b.score);

  for (const { slug, city } of cityMatches.slice(0, 3)) {
    const count = index.filter((e) => e.cityHits.includes(slug)).length;
    suggestions.push({
      kind: "city",
      label: city.name,
      sublabel: `${city.region}${count ? ` · ${count} ${count === 1 ? "tour" : "tours"}` : ""}`,
      slug,
      count,
    });
    suggestions.push({
      kind: "phrase",
      label: `Tours en ${city.name}`,
      sublabel: "Búsqueda de experiencias",
      query: city.name,
    });
    if (suggestions.length >= limit) break;
  }

  // 2. Product titles matching the query.
  if (suggestions.length < limit) {
    const tokens = q.split(/\s+/).filter(Boolean);
    const seen = new Set<string>();
    for (const entry of index) {
      if (suggestions.length >= limit) break;
      if (!tokens.every((t) => entry.haystack.includes(t))) continue;
      const key = entry.product.title.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      suggestions.push({
        kind: "product",
        label: entry.product.title,
        sublabel: entry.product.location ?? "Tour",
        productId: entry.product.id,
      });
    }
  }

  return suggestions.slice(0, limit);
}

// ---------------- Sorting -----------------------------------------------------

export type SortKey = "featured" | "price" | "popularity" | "duration";

export function sortProducts<T extends SearchableTour>(
  products: readonly T[],
  key: SortKey,
): T[] {
  const list = [...products];
  switch (key) {
    case "price":
      return list.sort(
        (a, b) =>
          (a.priceFrom ?? Number.POSITIVE_INFINITY) -
          (b.priceFrom ?? Number.POSITIVE_INFINITY),
      );
    case "duration":
      return list.sort(
        (a, b) =>
          (a.durationMinutes ?? Number.POSITIVE_INFINITY) -
          (b.durationMinutes ?? Number.POSITIVE_INFINITY),
      );
    case "popularity":
      // Bokun doesn't expose review counts; use # of categories + presence of
      // images + lower price-band as a pragmatic proxy.
      return list.sort((a, b) => popularityScore(b) - popularityScore(a));
    case "featured":
    default:
      // Preserve Bokun's natural order (operator-curated), but push
      // unavailable items to the end.
      return list.sort((a, b) => Number(b.available) - Number(a.available));
  }
}

function popularityScore(p: SearchableTour): number {
  let s = 0;
  s += (p.categories?.length ?? 0) * 2;
  s += (p.images?.length ?? 0);
  if (p.available) s += 5;
  if (p.priceFrom && p.priceFrom < 1500) s += 3;
  return s;
}

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "featured", label: "Destacados" },
  { key: "price", label: "Precio" },
  { key: "popularity", label: "Popularidad" },
  { key: "duration", label: "Duración" },
];
